import { algoliasearch, type SearchResponse } from 'algoliasearch';
import { makeServiceTerms } from '@/utils/algolia';
import { SearchLocation } from '@/utils/search';
import { clientEnv } from '@/config/env/client';
import { AlgoliaClinicV2 } from '@/types/algolia-types';
import { ChannelFilterType, DateFilterType, FilterProps, SortingListingType } from '@/api/algolia/types';

export const generateAlgoliaClient = (key: string) => {
  const appId = clientEnv.NEXT_PUBLIC_ALGOLIA_APP_ID;
  const CLINIC_IDX = clientEnv.NEXT_PUBLIC_ALGOLIA_INDEX;

  const client = algoliasearch(appId, key);

  return {
    client,
    clinicIndex: CLINIC_IDX,
    servicePriceAscIndex: `${CLINIC_IDX}_service_price_asc`,
    firstAvailabilityAscIndex: `${CLINIC_IDX}_first_availability_asc`,
    noDistinctIndex: `${CLINIC_IDX}_centri-medici`,
    nomenclatoreIndex: 'nomenclatore',
  };
};

export const publicAlgoliaClient = generateAlgoliaClient(clientEnv.NEXT_PUBLIC_ALGOLIA_SEARCH_KEY);

const dateFilterMs = {
  [DateFilterType.three_days]: 3,
  [DateFilterType.seven_days]: 7,
};

const getTimestampInDays = (daysFromNow: number): number => {
  const now = new Date();
  const targetDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + daysFromNow, 0, 0, 0, 0));
  return targetDate.getTime();
};

const sortingIndexes = {
  [SortingListingType.distanceAsc]: publicAlgoliaClient.clinicIndex,
  [SortingListingType.priceAsc]: publicAlgoliaClient.servicePriceAscIndex,
  [SortingListingType.firstAvailabilityAsc]: publicAlgoliaClient.firstAvailabilityAscIndex,
  [SortingListingType.noDistinct]: publicAlgoliaClient.noDistinctIndex,
};

type AlgoliaSearchRequestParams = {
  text: string;
  page?: number;
  filterObj?: null | FilterProps;
  hitsPerPage?: number;
  searchLocation?: null | SearchLocation;
  order?: SortingListingType;
  defaultIndex?: string;
  analyticsTags?: string[];
};

export type AlgoliaSuggestionsRequestParams = {
  text: string;
  filterObj?: null | FilterProps;
};

const getFacetFilters = (filterObj: FilterProps | null) => {
  return [
    ...(filterObj?.specialization ? [`specializations.macroSpeciality:${filterObj?.specialization}`] : []),
    ...(filterObj?.doctorSex ? [`specializations.doctor.sex:${filterObj?.doctorSex}`] : []),
    ...(filterObj?.license ? [`license:${filterObj?.license}`] : []),
    // char '-' negate the facet filter https://www.algolia.com/doc/api-reference/api-parameters/facetFilters/#usage-notes
    ...(filterObj?.excludeDomicilary ? [`specializations.channels:-DOMICILIARY`] : []),
    ...(filterObj?.excludeOnline ? [`specializations.channels:-VIDEO`] : []),
    ...(filterObj?.excludeOnsite ? [`specializations.channels:-STUDIO`] : []),
    ...(filterObj?.paymentType ? [`availablePayments:${filterObj.paymentType}`] : []),
    ...(filterObj?.availableFlowTypes ? [`availableFlowTypes:${filterObj.availableFlowTypes}`] : []),
    ...(filterObj?.channel
      ? [
          filterObj.channel.map(selectedChannel => {
            if (selectedChannel === ChannelFilterType.domiciliary) {
              return `domiciliaryCare:true`;
            }
            return `specializations.specialityChannels:${selectedChannel}`;
          }),
        ]
      : []),
    ...(filterObj?.ambulatoryId ? [`ambulatoryId:${filterObj.ambulatoryId}`] : []),
    ...(filterObj?.groupSlug ? [`groupSlug:${filterObj.groupSlug}`] : []),
  ];
};

// Used in search page
export const getAlgoliaSearchResponse = async ({
  text,
  searchLocation = null,
  order,
  page = 0,
  filterObj = null,
  hitsPerPage = 20,
  defaultIndex = publicAlgoliaClient.clinicIndex,
  analyticsTags = ['default-booking'],
}: AlgoliaSearchRequestParams): Promise<SearchResponse<AlgoliaClinicV2>> => {
  const indexName = order ? sortingIndexes[order] : defaultIndex;
  return await publicAlgoliaClient.client.searchSingleIndex<AlgoliaClinicV2>({
    indexName,
    searchParams: {
      query: text,
      page,
      numericFilters: [
        ...(filterObj?.dateFilter ? [`firstAvailableVisitTimestamp < ${getTimestampInDays(dateFilterMs[filterObj.dateFilter])}`] : []),
        ...(filterObj?.excludePrice0 ? [`specializations.price > 500`] : []), // To exclude blood tests
      ],
      facets: ['specializations.price', 'firstAvailableVisitTimestamp'],
      facetFilters: getFacetFilters(filterObj),
      hitsPerPage,
      getRankingInfo: true,
      analyticsTags,
      aroundRadius: !!searchLocation?.latitude && !!searchLocation?.longitude ? 30000 : 'all',
      ...(!!searchLocation?.latitude && !!searchLocation?.longitude
        ? { aroundLatLng: `${searchLocation.latitude}, ${searchLocation.longitude}` }
        : { aroundLatLngViaIP: true }),
      // alternativesAsExact: ['singleWordSynonym'],
    },
  });
};

// Used in autocomplete
export const getAlgoliaSearchSuggestions = async ({ text }: AlgoliaSuggestionsRequestParams) => {
  const searchResponse = await publicAlgoliaClient.client.searchSingleIndex<AlgoliaClinicV2>({
    indexName: publicAlgoliaClient.nomenclatoreIndex,
    searchParams: {
      query: text,
      analyticsTags: ['booking-nomenclatore'],
    },
  });

  const serviceTerms = makeServiceTerms(searchResponse);

  return [...serviceTerms];
};

// Used in autocomplete when a groupSlug is present (large medical groups)
export const getAlgoliaSearchSuggestionsGroupSlug = async ({ text, groupSlug }: { text: string; groupSlug: string }) => {
  const searchResponse = await publicAlgoliaClient.client.searchSingleIndex<AlgoliaClinicV2>({
    indexName: publicAlgoliaClient.clinicIndex,
    searchParams: {
      query: text,
      facetFilters: [`groupSlug:${groupSlug}`],
      analyticsTags: ['booking-group-nomenclatore'],
    },
  });

  const serviceTerms = makeServiceTerms(searchResponse);

  return [...serviceTerms];
};

export type ClinicSearchResult = SearchResponse<AlgoliaClinicV2>;
